import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircle, AlertCircle } from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import FooterExpanded from '../components/landing/FooterExpanded';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Sprint 34.12.i — public status page. Anyone can visit /status.
 */
export default function StatusPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = () => {
      axios.get(`${API_URL}/api/status`)
        .then(r => setData(r.data))
        .catch(() => setData({ overall: 'unreachable', checks: {}, active_banners: [] }))
        .finally(() => setLoading(false));
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const overallColor = data?.overall === 'operational' ? 'text-success' : data?.overall === 'degraded' ? 'text-aura-gold' : 'text-error';

  return (
    <div className="min-h-screen bg-midnight text-text-light">
      <SiteHeader />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
        <h1 className="text-4xl font-serif font-medium mb-2">System Status</h1>
        <p className="text-text-muted mb-8">Real-time view of Memouraa's services. Refreshes every 30 seconds.</p>

        {loading ? (
          <div className="text-text-muted">Loading…</div>
        ) : (
          <div className="space-y-6">
            <div className={`rounded-2xl border p-6 ${data.overall === 'operational' ? 'border-success/30 bg-success/5' : 'border-error/30 bg-error/5'}`}>
              <div className="flex items-center gap-3">
                {data.overall === 'operational' ? <CheckCircle className={`w-8 h-8 ${overallColor}`} /> : <AlertCircle className={`w-8 h-8 ${overallColor}`} />}
                <div>
                  <div className={`text-2xl font-medium ${overallColor} capitalize`}>{data.overall}</div>
                  <div className="text-text-muted text-sm">Last updated {new Date(data.timestamp || Date.now()).toLocaleString()}</div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 p-6">
              <h2 className="text-text-light font-semibold mb-4">Components</h2>
              <div className="space-y-2">
                {Object.entries(data.checks).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <span className="text-text-light capitalize">{k}</span>
                    <span className={`flex items-center gap-2 text-sm font-medium ${v === 'ok' ? 'text-success' : 'text-error'}`}>
                      <span className={`w-2 h-2 rounded-full ${v === 'ok' ? 'bg-success' : 'bg-error animate-pulse'}`} />
                      {v === 'ok' ? 'Operational' : v === 'fail' ? 'Down' : 'Unknown'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {data.active_banners && data.active_banners.length > 0 && (
              <div className="rounded-2xl border border-aura-gold/30 bg-aura-gold/5 p-6">
                <h2 className="text-aura-gold font-semibold mb-3">Active notices</h2>
                <ul className="space-y-2">
                  {data.active_banners.map((b, i) => (
                    <li key={i} className="text-text-light text-sm flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {b.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
      <FooterExpanded />
    </div>
  );
}
